/**
 * Establishments management page.
 * List with {@link CollapseReplaceWrapper} for create/edit forms.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/forms/Button';
import { CollapseReplaceWrapper } from '@/components/common/CollapseReplaceWrapper';
import { EstablishmentsDataTable } from '@/components/establishments/EstablishmentsDataTable';
import { CreateEstablishmentView } from '@/components/establishments/CreateEstablishmentView';
import { EditEstablishmentView } from '@/components/establishments/EditEstablishmentView';
import { useDetailsView } from '@/hooks/useDetailsView';
import { useViewMode } from '@/hooks/useViewMode';
import { ManagementPage } from '@/components/common/ManagementPage';
import { usePageActions } from '@/hooks/usePageActions';
import type { Establishment } from '@/types/establishment.types';

/** Which form is mounted in the collapse wrapper. */
type ActiveForm = 'create' | 'edit' | null;

/** Establishments list with CollapseReplace create/edit. */
export const EstablishmentsManagementPage: React.FC = () => {
  const { canCreate, canEditRow, canDeleteRow, canExportCsv } = usePageActions('establishments');
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeForm, setActiveForm] = React.useState<ActiveForm>(null);
  const [editEstablishmentId, setEditEstablishmentId] = React.useState<string | null>(null);
  const { viewMode, setViewMode } = useViewMode('establishments-management');
  const { openRowId, openRow, setOpenRow, closeRow } = useDetailsView();
  const contentRef = React.useRef<HTMLDivElement>(null);

  /** Deep-link: open create/view via `?create=` / `?view=`, then strip the params. */
  React.useEffect(() => {
    const viewId = searchParams.get('view');
    const shouldCreate = searchParams.get('create') === 'true';

    if (viewId) {
      setOpenRow(viewId);
      setActiveForm(null);
      setEditEstablishmentId(null);
      searchParams.delete('view');
      setSearchParams(searchParams, { replace: true });
    }

    if (shouldCreate) {
      setActiveForm('create');
      setEditEstablishmentId(null);
      closeRow();
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, closeRow, openRow]);

  /** Swap the table for the edit form and close any open details row. */
  const handleEditEstablishment = (establishment: Establishment) => {
    setEditEstablishmentId(establishment.id);
    setActiveForm('edit');
    closeRow();
  };

  /** Close the form and drop the edit id so the next open starts clean. */
  const closeForm = React.useCallback(() => {
    setActiveForm(null);
    setEditEstablishmentId(null);
  }, []);

  /** Close the form, refresh the list, then reload so derived establishment data is fresh. */
  const handleSuccess = React.useCallback(() => {
    setActiveForm(null);
    setEditEstablishmentId(null);
    void queryClient.invalidateQueries({ queryKey: ['establishments'] });
    window.location.reload();
  }, [queryClient]);

  /** Pick the collapse-wrapper form from `activeForm`. */
  const formContent = React.useMemo(() => {
    if (activeForm === 'create') {
      return <CreateEstablishmentView onClose={closeForm} onSuccess={handleSuccess} />;
    }
    if (activeForm === 'edit' && editEstablishmentId) {
      return (
        <EditEstablishmentView
          key={editEstablishmentId}
          establishmentId={editEstablishmentId}
          onClose={closeForm}
          onSuccess={handleSuccess}
        />
      );
    }
    return null;
  }, [activeForm, editEstablishmentId, closeForm, handleSuccess]);

  /** Scroll the page to the form when it first opens. */
  const prevActiveFormRef = React.useRef(activeForm);
  React.useEffect(() => {
    if (prevActiveFormRef.current !== activeForm && activeForm !== null && contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevActiveFormRef.current = activeForm;
  }, [activeForm]);

  return (
    <ManagementPage
      title="Gestion des Établissements"
      subtitle="Créez, modifiez et gérez tous vos établissements"
      contentRef={contentRef}
      actions={
        canCreate ? (
          <Button
            preset="create-entity"
            onClick={() => {
              setActiveForm('create');
              setEditEstablishmentId(null);
              closeRow();
            }}
          >
            Créer un établissement
          </Button>
        ) : undefined
      }
    >
      <CollapseReplaceWrapper
        mode="replace"
        isOpen={activeForm !== null}
        collapsePosition="top"
        formContent={formContent}
      >
        <EstablishmentsDataTable
          expandedRowId={openRowId}
          onToggleRow={openRow}
          onCloseDetails={closeRow}
          onEditEstablishment={handleEditEstablishment}
          enableFilters={true}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          pageActions={{ canEditRow, canDeleteRow, canExportCsv }}
        />
      </CollapseReplaceWrapper>
    </ManagementPage>
  );
};
